import { useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';

import Avatar from '../../components/Avatar';
import Button from '../../components/Button';
import Tooltip from '../../components/Tooltip';
import { pluralize } from '../../lib/copy';
import { formatDuration } from '../../lib/time';
import { getTagStyles, DEFAULT_TAG_COLOR } from '../../lib/colors';
import Tag from '../../svgs/Tag';
import {
  ProgramTag,
  TimeSeriesInterval,
  useClientsPerformanceSummaryQuery,
} from '../../../generated/graphql';
import type { PractitionerAssignmentsQuery } from '../../../generated/graphql';

import InsightsPagination from './InsightsPagination';
import type { ClientsDateRange } from './insightsFilters';

const MAX_VISIBLE_TAGS = 3;
export const CLIENT_TABLE_PAGE_SIZE = 25;

type AssignedClientFromQuery =
  PractitionerAssignmentsQuery['practitionerAssignments'][number]['assignedClients'][number];

interface ClientTableProps {
  clients: AssignedClientFromQuery[];
  practitionerId: string;
  /** null while the range is unknown (e.g. the headline metrics failed): rows show without stats */
  dateRange: ClientsDateRange | null;
}

const LoadingCell = () => (
  <span className="inline-block animate-pulse rounded px-4">-</span>
);

const EmptyCell = () => <span className="text-neutral-100">-</span>;

const formatDurationCell = (
  value: number | null | undefined,
  loading: boolean,
) => {
  if (loading) return <LoadingCell />;
  if (value == null) return <EmptyCell />;
  return formatDuration(value);
};

const formatDateCell = (value: number | null | undefined, loading: boolean) => {
  if (loading) return <LoadingCell />;
  if (value == null) return <EmptyCell />;
  return new Date(value).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatMetricCell = (
  value: number | null | undefined,
  loading: boolean,
) => {
  if (loading) return <LoadingCell />;
  if (value == null || value === 0) return <EmptyCell />;
  return value;
};

const ClientTable = ({
  clients,
  practitionerId,
  dateRange,
}: ClientTableProps) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || isVisible) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true);
        }
      },
      { rootMargin: '200px 0px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isVisible]);

  // Reset to first page when the assigned client list or date range changes
  useEffect(() => {
    setCurrentPage(1);
  }, [clients, dateRange?.startDate, dateRange?.endDate]);

  const totalCount = clients.length;
  const totalPages = Math.max(
    1,
    Math.ceil(totalCount / CLIENT_TABLE_PAGE_SIZE),
  );
  const safePage = Math.min(currentPage, totalPages);

  const pageClients = useMemo(() => {
    const start = (safePage - 1) * CLIENT_TABLE_PAGE_SIZE;
    return clients.slice(start, start + CLIENT_TABLE_PAGE_SIZE);
  }, [clients, safePage]);

  const { data, loading } = useClientsPerformanceSummaryQuery({
    variables: {
      input: {
        practitionerId,
        clientIds: pageClients.map((client) => client.id),
        // Required by the schema; the server aggregates over dateRange only.
        interval: TimeSeriesInterval.Month,
        dateRange: {
          startDate: dateRange?.startDate ?? '',
          endDate: dateRange?.endDate ?? '',
        },
      },
    },
    skip: !isVisible || pageClients.length === 0 || !dateRange,
  });

  const clientPerformanceMap = useMemo(() => {
    if (!data?.clientsPerformanceSummary?.clients) return {};
    return data.clientsPerformanceSummary.clients.reduce(
      (acc, client) => ({
        ...acc,
        [client.id]: client,
      }),
      {} as Record<
        string,
        typeof data.clientsPerformanceSummary.clients[number]
      >,
    );
  }, [data]);

  const totals = data?.clientsPerformanceSummary?.totals;
  const metricsLoading = !isVisible || loading;
  const isPaginated = totalCount > CLIENT_TABLE_PAGE_SIZE;
  // Totals from the API cover only the current page's clientIds — label them
  // accordingly so they aren't read as practitioner-wide aggregates.
  const totalsLabel = isPaginated
    ? `Page totals (${pageClients.length} of ${totalCount} clients)`
    : pluralize(totalCount, 'active client', 'active clients');

  const navigateToClientProfile = (programId?: string | null) => {
    if (!programId) {
      return;
    }
    navigate(`/clients/${programId}`);
  };

  return (
    <div ref={containerRef}>
      <table className="w-full">
        <thead>
          <tr
            className={classNames(
              'border-b border-neutral-75',
              'text-left text-small-caption font-medium text-neutral-150',
            )}
          >
            <th className="min-w-[300px] pl-4">Client</th>
            <th className="min-w-[80px] pl-0">Alerts</th>
            <th className="min-w-[80px] pl-0">Sessions</th>
            <th className="min-w-[100px] pl-0">
              <Tooltip
                content={
                  <>
                    <div className="mb-1 font-medium">
                      Average Response Time
                    </div>
                    <div className="text-xs font-normal">
                      The average time it takes for a practitioner to respond to
                      a client's chat message.
                    </div>
                  </>
                }
                placement="bottom"
                showArrow
              >
                <span className="underline decoration-dotted underline-offset-4">
                  ART
                </span>
              </Tooltip>
            </th>
            <th className="min-w-[120px] pl-0">Added</th>
            <th className="w-[320px] pl-0">Tags</th>
            <th className="w-[3.75rem]">{/* Overflow menu can go here */}</th>
          </tr>
        </thead>
        <tbody>
          <tr className="bg-neutral-30 font-medium text-neutral-125">
            <td className="pl-4 font-sans text-caption">{totalsLabel}</td>
            <td className="pl-0">
              {formatMetricCell(totals?.totalAlerts, metricsLoading)}
            </td>
            <td className="pl-0">
              {formatMetricCell(totals?.totalSessions, metricsLoading)}
            </td>
            <td className="pl-0">
              {formatDurationCell(totals?.averageResponseTime, metricsLoading)}
            </td>
            <td colSpan={3} className="w-[3.75rem]" />
          </tr>
          {pageClients.map((client) => {
            const performanceData = clientPerformanceMap[client.id];
            return (
              <tr
                key={client.id}
                className="whitespace-nowrap font-sans text-caption text-neutral-110"
              >
                <td className="w-1/6 py-4 pl-4">
                  <div
                    className="flex cursor-pointer flex-row items-center gap-x-4"
                    onClick={() =>
                      navigateToClientProfile(client.latestProgramId)
                    }
                  >
                    <Avatar
                      size="medium-plus"
                      name={client.name}
                      imageUrl={client.profileImageUrl}
                    />
                    <span
                      className="w-[220px] overflow-hidden text-ellipsis font-serif text-extra-small text-green-150"
                      title={client.name}
                    >
                      {client.name}
                    </span>
                  </div>
                </td>
                <td className="pl-0">
                  {formatMetricCell(
                    performanceData?.metrics.totalAlerts,
                    metricsLoading,
                  )}
                </td>
                <td className="pl-0">
                  {formatMetricCell(
                    performanceData?.metrics.totalSessions,
                    metricsLoading,
                  )}
                </td>
                <td className="pl-0">
                  {formatDurationCell(
                    performanceData?.metrics.averageResponseTime,
                    metricsLoading,
                  )}
                </td>
                <td className="pl-0">
                  {formatDateCell(performanceData?.startedAt, metricsLoading)}
                </td>
                <td className="pl-0">
                  {!metricsLoading && performanceData ? (
                    <div className="flex flex-row flex-nowrap items-center gap-1 overflow-hidden">
                      {performanceData.tags
                        .slice(0, MAX_VISIBLE_TAGS)
                        .map((tag: ProgramTag) => (
                          <Button
                            key={tag.id}
                            size="smaller"
                            theme="secondary"
                            title={tag.name}
                            IconComponent={Tag}
                            iconPosition="left"
                            noBackground={true}
                            className="hover:opacity-90"
                            style={getTagStyles(tag.color ?? DEFAULT_TAG_COLOR)}
                          />
                        ))}
                      {performanceData.tags.length > MAX_VISIBLE_TAGS && (
                        <Tooltip
                          content={
                            <div className="flex max-w-xs flex-wrap gap-1">
                              {performanceData.tags.map((tag: ProgramTag) => (
                                <Button
                                  key={tag.id}
                                  size="smaller"
                                  theme="secondary"
                                  title={tag.name}
                                  IconComponent={Tag}
                                  iconPosition="left"
                                  noBackground={true}
                                  className="hover:opacity-90"
                                  style={getTagStyles(
                                    tag.color ?? DEFAULT_TAG_COLOR,
                                  )}
                                />
                              ))}
                            </div>
                          }
                          placement="bottom-start"
                          showArrow
                        >
                          <span className="inline-flex cursor-pointer items-center rounded-full bg-neutral-50 px-2 py-1.5 text-small-caption font-semibold hover:bg-neutral-75">
                            +{performanceData.tags.length - MAX_VISIBLE_TAGS}
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    '-'
                  )}
                </td>
                <td className="w-[3.75rem]" />
              </tr>
            );
          })}
        </tbody>
      </table>

      {totalCount > CLIENT_TABLE_PAGE_SIZE && (
        <InsightsPagination
          hasPreviousPage={safePage > 1}
          hasNextPage={safePage < totalPages}
          totalCount={totalCount}
          itemCountOnPage={pageClients.length}
          currentPage={safePage}
          pageSize={CLIENT_TABLE_PAGE_SIZE}
          onPreviousPage={() => setCurrentPage((page) => Math.max(1, page - 1))}
          onNextPage={() =>
            setCurrentPage((page) => Math.min(totalPages, page + 1))
          }
        />
      )}
    </div>
  );
};

export default ClientTable;
